import { useEffect, useMemo, useRef, useState } from "react";
import BaseCard from "../../components/ui/BaseCard";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Chip, Avatar, Menu, MenuItem, Tooltip, Badge } from "@mui/material"; // import Avatar for profile pic
import BaseDropdownV2 from "../../components/ui/BaseDropdownV2";
import { useNavigate } from "react-router-dom";
import ApproveEngineerModal from "./components/ApproveEngineerModal";
import SendEmailToEngineerModal from "./components/SendEmailToEngineerModal";
import { fetchAllNewlyCreatedUsersByRole } from "../../services/api/admin";
import BaseTextField from "../../components/ui/BaseTextField";
import { debounce, formatPhoneNumber } from "../../utils/helpers";
import { UserType } from "../../utils/enum";
import { INCOMPLETE_CHIP_COLOR } from "../../utils/variables";
import InviteEngineerModal from "./components/InviteEngineerModal";
import BaseConfirmModal from "../../components/ui/BaseConfirmModal";
import { showToast } from "../../store/toast/toastSlice";
import { useDispatch, useSelector } from "react-redux";
import { activateEngineer, deactivateEngineer } from "../../services/api/admin";
import { setEngineers, updateEngineerStatus } from "../../store/engineers/engineerSlice";
import { setAdminFilters } from "../../store/filters/adminFiltersSlice";
import AdminFilters from "./AdminFilters";
import { deleteUser } from "../../services/api/user";
import BaseWrapper from "../../components/ui/BaseWrapper";
import IdentityFiles from "../../components/ui/IdentityFiles";
import dayjs from "dayjs";

const menuOptions = [
  {
    label: "View Profile",
    value: "VIEW",
  },
  {
    label: "Approve",
    value: "APPROVE",
  },
  {
    label: "Activate",
    value: "APPROVED",
  },
  {
    label: "Deactivate",
    value: "DEACTIVATED",
  },
  // {
  //   label: "Delete",
  //   value: "DELETE",
  //   icon: <i class="fa-solid fa-toggle-off"></i>,
  // },
  {
    label: "Send Email",
    value: "EMAIL",
  },
];

const EngineersTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const engineerFilters = useSelector((s) => {
    return s.adminFilters.engineer;
  });
  const [filtersChanged, setFiltersChanged] = useState(false);

  const { page, pageSize } = engineerFilters.pagination;

  const [isApproveEngineerModalOpen, setIsApproveEngineerModalOpen] = useState(false);
  const [isSendEmailToEngineerModalOpen, setIsSendEmailToEngineerModalOpen] = useState(false);
  const [isInviteEngineerModalOpen, setIsInviteEngineerModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const baseQueryParams = {
    role: UserType.FREELANCER,
    // Vendor owners run a company rather than take jobs, so they aren't listed here.
    excludeCompanyOwners: true,
    page: page + 1,
    limit: pageSize,
    search: "",
  };
  const [queryParams, setQueryParams] = useState(baseQueryParams);
  const [paginationModel, setPaginationModel] = useState({
    page,
    pageSize,
  });
  const [contextMenu, setContextMenu] = useState(null);
  // The grid renders the redux `engineers` list — see BaseDataTableV2 below.
  const engineers = useSelector((state) => state.engineers.list);
  const [rowCount, setRowCount] = useState(null);
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [isConfirmActivateOpen, setIsConfirmActivateOpen] = useState(false);
  const [isConfirmDeactivateOpen, setIsConfirmDeactivateOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const filtersPayload = useMemo(() => {
    return {
      country: engineerFilters.country?.label || undefined,
      city: engineerFilters.city?.label || undefined,
      status: engineerFilters.status || undefined,
      email: engineerFilters.email || undefined,
      fullName: engineerFilters.fullName || undefined,
      startDate: engineerFilters.startDate || undefined,
      endDate: engineerFilters.endDate || undefined,
    };
  }, [engineerFilters]);

  const {
    isPending: isfetchAllNewlyCreatedUsersByRoleApiLoading,
    data: newUsersListData,
    refetch: refetchEngineers,
  } = useQuery({
    queryKey: ["newUsers", queryParams, filtersPayload],
    queryFn: () => fetchAllNewlyCreatedUsersByRole({ ...queryParams, ...filtersPayload }),
  });

  const { data: engineerOptionsData } = useQuery({
    queryKey: ["engineerFilterOptions"],
    queryFn: () =>
      fetchAllNewlyCreatedUsersByRole({
        role: UserType.FREELANCER,
        excludeCompanyOwners: true,
        limit: 500,
      }),
    enabled: isFilterDrawerOpen,
    staleTime: 5 * 60 * 1000,
  });

  const nameOptions = useMemo(
    () =>
      [
        ...new Set(
          (engineerOptionsData?.data?.data || [])
            .map((user) => `${user.firstName || ""} ${user.lastName || ""}`.trim())
            .filter(Boolean),
        ),
      ]
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name })),
    [engineerOptionsData],
  );
  const applyEngineerStatusChange = (id, status) => {
    dispatch(updateEngineerStatus({ id, status }));
    queryClient.invalidateQueries({ queryKey: ["newUsers"] });
  };

  const { mutateAsync: activateEngineerfunction, isPending: isActivating } = useMutation({
    mutationFn: (id) => activateEngineer(id),
    onSuccess: (_, variables) => {
      applyEngineerStatusChange(variables, "APPROVED");
      dispatch(
        showToast({
          message: "Engineer activated successfully",
          type: "success",
        }),
      );
      setIsConfirmActivateOpen(false);
    },
  });
  const { mutateAsync: deactivateEngineerfunction, isPending: isDeactivating } = useMutation({
    mutationFn: (id) => deactivateEngineer(id),
    onSuccess: (_, variables) => {
      applyEngineerStatusChange(variables, "DEACTIVATED");
      dispatch(
        showToast({
          message: "Engineer deactivated successfully",
          type: "success",
        }),
      );
      setIsConfirmDeactivateOpen(false);
    },
  });
  useEffect(() => {
    if (filtersChanged) {
      setFiltersChanged(false);
    }
  }, [filtersChanged]);
  useEffect(() => {
    if (filtersChanged) {
      const newPage = 0;
      const newPageSize = engineerFilters.pagination.pageSize || 20;

      setPaginationModel({
        page: newPage,
        pageSize: newPageSize,
      });

      setQueryParams((prev) => ({
        ...prev,
        page: newPage + 1,
        limit: newPageSize,
      }));

      setFiltersChanged(false);
    }
  }, [filtersChanged, engineerFilters.pagination.pageSize]);

  useEffect(() => {
    setPaginationModel({ page, pageSize });
    setQueryParams((prev) => ({ ...prev, page: page + 1, limit: pageSize }));
  }, [page, pageSize]);

  useEffect(() => {
    if (newUsersListData?.data?.data) {
      const mappedData = newUsersListData.data.data.map((user) => {
        const primaryAddress = user.addresses?.[0] || {};
        return {
          ...user,
          address: primaryAddress.addressLine1 || "-",
          city: primaryAddress.city || "-",
          state: primaryAddress.state || "-",
          zipCode: primaryAddress.zipCode || "-",
          country: primaryAddress.country || "-",
          previousStatus: user.status,
        };
      });
      dispatch(setEngineers(mappedData));
      setRowCount(newUsersListData?.data?.totalCount || 0);
    }
  }, [newUsersListData]);

  const handleContextMenu = (event, item) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
    setSelectedEngineer(item);
  };
  const handleClose = () => {
    setContextMenu(null);
    setSelectedEngineer(null);
  };
  const renderHeaderWithIcon = (title) => (
    <div className="flex items-center gap-2">
      {" "}
      <span className="text-[#525354] font-medium">{title}</span>{" "}
    </div>
  );

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.7,
      minWidth: 80,
      renderHeader: () => renderHeaderWithIcon("ID"),
      renderCell: (params) => (
        <p
          onClick={() => navigate(`/user/details/profileInformation/${params.row.id}`)}
          onContextMenu={(e) => handleContextMenu(e, params.row)}
          className={` font-normal text-primary cursor-pointer`}
        >
          {params?.value}
        </p>
      ),
    },

    {
      field: "firstName",
      headerName: "Full Name",
      flex: 1.1,
      minWidth: 150,
      renderHeader: () => renderHeaderWithIcon("Full Name"),
      renderCell: (params) => (
        <div
          onClick={() => navigate(`/user/details/profileInformation/${params.row.id}`)}
          onContextMenu={(e) => handleContextMenu(e, params.row)}
          className="flex items-center gap-2 cursor-pointer"
        >
          <Avatar
            src={params.row.profilePicture || "/default-avatar.png"}
            alt={`${params.row.firstName} ${params.row.lastName}`}
            sx={{ width: 32, height: 32 }}
          />
          <span className="text-primary">
            {params.row.firstName} {params.row.lastName}
          </span>
        </div>
      ),
    },

    {
      field: "engineerType",
      headerName: "Type",
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderHeader: () => renderHeaderWithIcon("Type"),
      renderCell: (params) =>
        params.row.freelanceCompany?.id ? (
          <Tooltip title="Open vendor">
            <p
              className="table-font-size text-primary cursor-pointer truncate"
              onClick={() =>
                navigate(`/admin/review/freelanceCompanies/${params.row.freelanceCompany.id}`)
              }
            >
              {params.row.freelanceCompany.name} (ID: {params.row.freelanceCompany.id})
            </p>
          </Tooltip>
        ) : (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
            Independent
          </span>
        ),
    },

    {
      field: "address",
      headerName: "Address",
      renderHeader: () => renderHeaderWithIcon("Address"),
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => <p>{params.value || "-"}</p>,
    },
    {
      field: "city",
      headerName: "City",
      renderHeader: () => renderHeaderWithIcon("City"),
      flex: 0.7,
      minWidth: 150,
      renderCell: (params) => (
        <p>{params.value !== "null" && params.value !== null ? params.value : "-"}</p>
      ),
    },
    {
      field: "state",
      headerName: "State",
      renderHeader: () => renderHeaderWithIcon("State"),
      flex: 0.7,
      minWidth: 150,
      renderCell: (params) => (
        <p>{params.value !== "null" && params.value !== null ? params.value : "-"}</p>
      ),
    },
    {
      field: "zipCode",
      headerName: "Zip / Post Code",
      renderHeader: () => renderHeaderWithIcon("Zip / Post Code"),
      flex: 0.7,
      minWidth: 150,
      renderCell: (params) => {
        const value = params.value;
        return (
          <p>
            {value === null || value === undefined || value === "" || value === "null"
              ? "-"
              : value}
          </p>
        );
      },
    },
    {
      field: "country",
      headerName: "Country",
      renderHeader: () => renderHeaderWithIcon("Country"),
      flex: 0.7,
      minWidth: 150,
      renderCell: (params) => (
        <p>{params.value !== "null" && params.value !== null ? params.value : "-"}</p>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      renderHeader: () => renderHeaderWithIcon("Email"),

      flex: 0.9,

      minWidth: 150,
      renderCell: (params) => (
        <p>{params.value !== "null" && params.value !== null ? params.value : "-"}</p>
      ),
    },
    {
      field: "identity",
      headerName: "Identity",
      renderHeader: () => renderHeaderWithIcon("Identity"),
      flex: 0.7,
      minWidth: 150,
      sortable: false,
      // What the engineer uploaded under Verify your Identity.
      renderCell: (params) => <IdentityFiles documents={params.row.identityDocuments} />,
    },
    {
      field: "MobileNumber",
      headerName: "Mobile Number",
      renderHeader: () => renderHeaderWithIcon("Mobile Number"),
      flex: 0.7,
      minWidth: 150,
      renderCell: (params) => <p>{formatPhoneNumber(params.row.phoneNumber) || "-"}</p>,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      renderHeader: () => renderHeaderWithIcon("Created At"),
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => (
        <p>{params.value ? dayjs(params.value).format("DD/MM/YYYY") : "-"}</p>
      ),
    },

    {
      field: "status",
      headerName: "Status",
      renderHeader: () => renderHeaderWithIcon("Status"),
      flex: 0.7,
      // Slightly wider than the other columns to leave room for the reminder
      // badge sitting off the chip's top-right corner.
      minWidth: 175,
      renderCell: (params) => {
        // Number of missing-documents emails sent. Rendered as a notification
        // badge on the status chip so "where they are" and "how often we've
        // chased" read as one thing. MUI hides the badge at 0 by default.
        const reminderCount = params.row.reminderCount ?? 0;

        const statusChip =
          params.value === "PENDING" ? (
            <Chip
              label="Pending"
              color="primary"
              size="small"
              variant="outlined"
              className="w-24 justify-center"
            />
          ) : params.value === "SUBMITTED" ? (
            <Chip
              label="Submitted"
              color="secondary"
              size="small"
              className="w-24 justify-center"
            />
          ) : params.value === "APPROVED" ? (
            <Chip label="Active" color="success" size="small" className="w-24 justify-center" />
          ) : params.value === "UNAPPROVE" ? (
            <Chip
              label="Incomplete"
              size="small"
              className="w-24 justify-center"
              sx={{ backgroundColor: INCOMPLETE_CHIP_COLOR, color: "#fff" }}
            />
          ) : (
            <Chip label="In-Active" color="error" size="small" className="w-24 justify-center" />
          );

        if (!reminderCount) return statusChip;

        // The reminder means two different things depending on where they are.
        // PENDING has never submitted anything, so the email was a nudge to
        // submit at all; every other status was reviewed and sent back, so the
        // email was about specific missing documents.
        const times = reminderCount === 1 ? "once" : `${reminderCount} times`;
        const reminderLabel =
          params.value === "PENDING"
            ? `Reminded ${times} to complete and submit their profile`
            : `Reminded ${times} about missing documents`;

        return (
          <Tooltip title={reminderLabel}>
            <Badge
              color="error"
              badgeContent={reminderCount}
              max={99}
              overlap="rectangular"
              sx={{
                ".MuiBadge-badge": {
                  minWidth: "18px",
                  height: "18px",
                  fontSize: "11px",
                  transform: "scale(1) translate(45%, -35%)",
                },
              }}
            >
              {statusChip}
            </Badge>
          </Tooltip>
        );
      },
    },

    {
      field: "actions",
      headerName: "Actions",
      renderHeader: () => renderHeaderWithIcon("Actions"),
      flex: 0.7,
      minWidth: 150,
      renderCell: (params) => {
        const userStatus = params?.row?.status;

        const filteredMenuOptions = menuOptions.filter((item) => {
          if (userStatus === "PENDING") {
            if (
              item.value === "APPROVE" ||
              item.value === "DEACTIVATED" ||
              item.value === "APPROVED"
            ) {
              return false;
            }
          }
          if (userStatus === "SUBMITTED") {
            if (item.value === "DEACTIVATED" || item.value === "APPROVED") {
              return false;
            }
          }
          if (
            userStatus === "APPROVED" &&
            (item.value === "APPROVED" || item.value === "APPROVE" || item.value === "EMAIL")
          ) {
            return false;
          }
          if (
            userStatus === "UNAPPROVE" &&
            (item.value === "APPROVED" ||
              item.value === "DEACTIVATED" ||
              item.value === "UNAPPROVE")
          ) {
            return false;
          }
          if (
            userStatus === "APPROVED" &&
            (item.value === "APPROVED" || item.value === "APPROVE" || item.value === "EMAIL")
          ) {
            return false;
          }

          if (
            userStatus === "DEACTIVATED" &&
            (item.value === "DEACTIVATED" || item.value === "APPROVE" || item.value === "EMAIL")
          ) {
            return false;
          }

          return true;
        });
        return (
          <BaseDropdownV2
            menuOptions={filteredMenuOptions}
            triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
            onItemClick={(item) => handleItemClick(item, params?.row)}
          />
        );
      },
    },
  ];

  const updateQueryParamsDebounced = useRef(
    debounce((query) => {
      setQueryParams((prev) => ({
        ...prev,
        search: query.trim() || "",
        page: 1,
      }));
    }, 500),
  ).current;

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateQueryParamsDebounced(query);
  };

  const handleCellClick = (params) => {
    if (params.field === "id" || params.field === "firstName") {
      navigate(`/user/details/profileInformation/${params?.row?.id}`);
    }

    return;
  };

  const handleItemClick = async (value, data) => {
    setSelectedEngineer(data);

    if (value === "APPROVE") {
      setIsApproveEngineerModalOpen(true);
    } else if (value === "EMAIL") {
      setIsSendEmailToEngineerModalOpen(true);
    } else if (value === "VIEW") {
      navigate(`/user/details/profileInformation/${data.id}`);
    } else if (value === "APPROVED") {
      setIsConfirmActivateOpen(true);
    } else if (value === "DEACTIVATED") {
      setIsConfirmDeactivateOpen(true);
    } else if (value === "DELETE") {
      setDeleteReason("");
      setIsConfirmDeleteOpen(true);
    }
  };

  const handleInviteButtonClick = async () => {
    setIsInviteEngineerModalOpen(true);
  };

  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
    setQueryParams((prev) => ({
      ...prev,
      page: newPaginationModel.page + 1,
      limit: newPaginationModel.pageSize,
    }));
    dispatch(
      setAdminFilters({
        module: "engineer",
        filterName: "pagination",
        data: {
          page: newPaginationModel.page,
          pageSize: newPaginationModel.pageSize,
        },
      }),
    );
  };
  const appliedFilters = useMemo(() => {
    const filters = [];
    if (engineerFilters?.startDate || engineerFilters?.endDate) {
      filters.push({
        prefix: "Date",
        value: `${engineerFilters.startDate || "Any"} - ${engineerFilters.endDate || "Any"}`,
        key: "dateRange",
      });
    }
    if (engineerFilters.country?.label)
      filters.push({ prefix: "Country", value: engineerFilters.country.label, key: "country" });

    if (engineerFilters.city?.label)
      filters.push({ prefix: "City", value: engineerFilters.city.label, key: "city" });

    const statusLabelMap = {
      PENDING: "Pending",
      SUBMITTED: "Submitted",
      APPROVED: "Active",
      ACTIVATED: "Active",
      DEACTIVATED: "In-Active",
      UNAPPROVE: "Incomplete",
    };

    if (engineerFilters.status) {
      const displayStatus = statusLabelMap[engineerFilters.status] || engineerFilters.status;
      filters.push({
        prefix: "Status",
        value: displayStatus,
        key: "status",
      });
    }

    if (engineerFilters.fullName)
      filters.push({ prefix: "Name", value: engineerFilters.fullName, key: "fullName" });

    if (engineerFilters.email)
      filters.push({ prefix: "Email", value: engineerFilters.email, key: "email" });

    return filters;
  }, [engineerFilters]);

  const handleRemoveFilter = (key) => {
    if (["fullName", "email", "status"].includes(key)) {
      dispatch(setAdminFilters({ module: "engineer", filterName: key, data: "" }));
    } else if (key === "city") {
      dispatch(
        setAdminFilters({
          module: "engineer",
          filterName: key,
          data: { value: "", label: "" },
        }),
      );
    } else if (key === "country") {
      dispatch(
        setAdminFilters({
          module: "engineer",
          filterName: key,
          data: { value: "", label: "" },
        }),
      );
      dispatch(
        setAdminFilters({
          module: "engineer",
          filterName: "city",
          data: { value: "", label: "" },
        }),
      );
    } else if (key === "dateRange") {
      dispatch(
        setAdminFilters({
          module: "engineer",
          filterName: "startDate",
          data: "",
        }),
      );
      dispatch(
        setAdminFilters({
          module: "engineer",
          filterName: "endDate",
          data: "",
        }),
      );
    }

    dispatch(
      setAdminFilters({
        module: "engineer",
        filterName: "pagination",
        data: { page: 0, pageSize: engineerFilters.pagination.pageSize },
      }),
    );

    setFiltersChanged(true);
  };
  const handleDeleteUser = async () => {
    if (!selectedEngineer) return;
    if (!deleteReason?.trim()) {
      dispatch(
        showToast({
          message: "Please provide a reason before deleting the user.",
          type: "error",
        }),
      );
      return;
    }
    try {
      setIsDeleting(true);
      await deleteUser(selectedEngineer.id, deleteReason);
      setIsConfirmDeleteOpen(false);
      refetchEngineers();
    } catch (error) {
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="border-b border-zinc-300 bg-white">
        <BaseWrapper>
          <div className="grid grid-cols-7 gap-y-3 py-[7px] items-center">
            <div className="md:col-span-3 col-span-7">
              <h3 className=" md:text-left text-center">Engineers</h3>
            </div>
            <div className="md:col-span-4 col-span-7 flex justify-end">
              <button
                className="bg-primary font-light text-sm text-white px-3 py-1.5 rounded shadow hover:bg-primary/90 transition"
                onClick={handleInviteButtonClick}
              >
                Invite Engineer
              </button>
            </div>
          </div>
        </BaseWrapper>
      </div>
      <BaseWrapper>
        <div className="bg-white rounded-lg px-5 py-3.5 shadow-md mt-4 sticky top-10 z-10">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 w-full">
            <div
              className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5 cursor-pointer"
              onClick={() => setIsFilterDrawerOpen(true)}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary">
                <i className="fa-light fa-filter text-white text-sm"></i>
              </span>
              <p className="text-primary ml-2 ">Add Filter</p>
            </div>
            <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
              <div className="flex gap-2">
                {appliedFilters.map((f) => {
                  return (
                    <div
                      key={(f.key || "") + (f.value || "") + (f.id || "")}
                      className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
                    >
                      <span className="text-primary">{f.prefix}:</span>&nbsp;
                      <p className="text-primary text-sm mx-1">{f.value}</p>
                      <button
                        onClick={() => handleRemoveFilter(f.key, f.id)}
                        className="ml-2 text-primary hover:text-red-500"
                        aria-label={`Remove ${f.prefix} ${f.value}`}
                      >
                        <i className="fas fa-times text-xs"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="w-full sm:w-[28%]">
              <BaseTextField
                placeholder={`Search by name, email or ID`}
                value={searchQuery} // Directly bind to searchQuery state
                onChange={handleSearchChange}
                startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
              />
            </div>
          </div>
        </div>
        <div className="max-h-[900px] overflow-y-auto mt-4">
          <BaseDataTableV2
            className="max-h-[calc(100vh-320px)] overflow-auto"
            rowHeight={38}
            columns={columns}
            data={engineers}
            isLoading={isfetchAllNewlyCreatedUsersByRoleApiLoading}
            disableColumnResize={false}
            hideFooter={false}
            pagination
            rowCount={rowCount}
            pageSizeOptions={[10, 20, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationChange}
            paginationMode="server"
            checkboxSelection
          />
        </div>
        {isConfirmDeleteOpen && (
          <BaseConfirmModal
            open={isConfirmDeleteOpen}
            onClose={() => setIsConfirmDeleteOpen(false)}
            onConfirm={handleDeleteUser}
            isLoading={isDeleting}
            title="Are you sure you want to delete this user?"
            description={
              <>
                <BaseTextField
                  label="Reason"
                  name="reason"
                  placeholder="Reason"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  labelShrink={false}
                  optional={false}
                  isClearable
                  isDefaultField={false}
                />
                <p className="mt-2 ">This action cannot be undone.</p>
              </>
            }
            confirmText="Delete"
            confirmButtonColor="error"
          />
        )}

        {isApproveEngineerModalOpen && (
          <ApproveEngineerModal
            isApproveEngineerModalOpen={isApproveEngineerModalOpen}
            setIsApproveEngineerModalOpen={setIsApproveEngineerModalOpen}
            onStatusChange={applyEngineerStatusChange}
            selectedEngineer={selectedEngineer}
          />
        )}

        {isSendEmailToEngineerModalOpen && (
          <SendEmailToEngineerModal
            setIsSendEmailToEngineerModalOpen={setIsSendEmailToEngineerModalOpen}
            isSendEmailToEngineerModalOpen={isSendEmailToEngineerModalOpen}
            onStatusChange={applyEngineerStatusChange}
            selectedEngineer={selectedEngineer}
          />
        )}

        {isInviteEngineerModalOpen && (
          <InviteEngineerModal
            isInviteEngineerModalOpen={isInviteEngineerModalOpen}
            setIsInviteEngineerModalOpen={setIsInviteEngineerModalOpen}
            onInvited={() => queryClient.invalidateQueries({ queryKey: ["newUsers"] })}
            selectedEngineer={selectedEngineer}
          />
        )}
        <BaseConfirmModal
          open={isConfirmActivateOpen}
          onClose={() => setIsConfirmActivateOpen(false)}
          onConfirm={() => activateEngineerfunction(String(selectedEngineer?.id))}
          isLoading={isActivating}
          title="Are you sure you want to activate this engineer?"
          description="Once activated, the engineer will regain access and their status will be set to Active."
          confirmText="Activate"
          confirmButtonColor="primary"
        />

        <BaseConfirmModal
          open={isConfirmDeactivateOpen}
          onClose={() => setIsConfirmDeactivateOpen(false)}
          onConfirm={() => deactivateEngineerfunction(String(selectedEngineer?.id))}
          isLoading={isDeactivating}
          title="Are you sure you want to deactivate this engineer?"
          description="Once deactivated, the engineer will lose access and their status will be set to Inactive."
          confirmText="Deactivate"
          confirmButtonColor="error"
        />
        {isFilterDrawerOpen && (
          <AdminFilters
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            fetchData={() => refetchEngineers()}
            isLoading={isfetchAllNewlyCreatedUsersByRoleApiLoading}
            setFiltersChanged={setFiltersChanged}
            entity="engineer"
            nameOptions={nameOptions}
          />
        )}
        <Menu
          open={contextMenu !== null}
          onClose={handleClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
          }
        >
          <MenuItem
            onClick={() => {
              navigate(`/user/details/profileInformation/${selectedEngineer?.id}`);
              handleClose();
            }}
          >
            <p>Open</p>
          </MenuItem>
          <MenuItem
            onClick={() => {
              window.open(`/user/details/profileInformation/${selectedEngineer?.id}`, "_blank");
              handleClose();
            }}
          >
            <p>Open in New Tab</p>
          </MenuItem>
        </Menu>
      </BaseWrapper>
    </>
  );
};

export default EngineersTable;
